import {
  Popover as UiPopover,
  PopoverContent,
  PopoverTrigger
} from '@Pages/pageRuntime/components/ui/Popover';
import type React from 'react';

type PopoverProps = {
  /**
   * Element that opens the popover.
   */
  trigger: React.ReactNode;

  /**
   * Popover body content.
   */
  content: React.ReactNode;

  /**
   * Controlled open state.
   */
  open?: boolean;

  /**
   * Called when the popover opens or closes.
   */
  onOpenChange?: (open: boolean) => void;

  /**
   * Alignment relative to the trigger.
   */
  align?: 'start' | 'center' | 'end';

  /**
   * Preferred side relative to the trigger.
   */
  side?: 'top' | 'right' | 'bottom' | 'left';

  className?: string;
};

/**
 * A small anchored panel for supplemental controls or details. Use for lightweight
 * contextual content; use Modal or Slideover for larger workflows.
 *
 * @example
 * <Sdk.Components.Popover trigger={<Sdk.Components.Button>Filters</Sdk.Components.Button>} content={<div>Filter controls</div>} />
 */
export function Popover({
  trigger,
  content,
  open,
  onOpenChange,
  align = 'center',
  side = 'bottom',
  className
}: PopoverProps) {
  return (
    <UiPopover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align={align} side={side} className={className}>
        {content}
      </PopoverContent>
    </UiPopover>
  );
}
